import React from 'react';
import { MarkdownRenderer } from '../../utils/MarkdownRenderer';
import { SummaryRenderer } from '../../utils/SummaryRenderer';
import { StructuredResultsRenderer, StreamingResultsRenderer } from '../StructuredResultsRenderer';
import { getToolMessage } from '../../utils/toolMessages';

interface MessageBubbleProps {
  message: any;
  onShowFeedback: () => void;
  t: any;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onShowFeedback, t }) => {
  if (message.type === 'tool_announcement') {
    return (
      <div className="flex justify-center">
        <div className="max-w-[80%] md:max-w-[70%] sm:max-w-[60%] bg-gray-100 border border-gray-200 rounded-lg px-2 py-1 text-center opacity-75">
          <div className="text-xs text-gray-500 font-normal flex items-center justify-center space-x-1">
            <span className="text-xs">{getToolMessage(message.content, t)}</span>
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {message.timestamp.toLocaleTimeString()}
          </div>
        </div>
      </div>
    );
  }

  if (message.type === 'system') {
    return (
      <div className="flex justify-center my-2">
        <div className="max-w-[90%] md:max-w-[80%] bg-blue-50 border-2 border-blue-200 rounded-lg px-4 py-2.5 text-center shadow-sm">
          <div className="text-sm text-blue-800 font-medium">
            {message.content}
          </div>
          <div className="text-xs text-blue-600 mt-1 opacity-75">
            {message.timestamp.toLocaleTimeString()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[92%] md:max-w-[88%] sm:max-w-[85%] rounded-lg md:rounded-xl px-2.5 md:px-3 sm:px-4 py-2 md:py-2.5 sm:py-3 ${
        message.type === 'user' 
          ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg' 
          : message.id === 'greeting'
            ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-gray-800 shadow-md border-2 border-blue-200'
            : 'bg-gradient-to-br from-slate-50 to-gray-50 text-gray-800 shadow-md border border-slate-200'
      }`}>
        {message.type === 'assistant' ? (
          <div className="text-sm md:text-base">
            {message.id === 'greeting' && (
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-base md:text-lg">🤖</span>
                <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  {t('chat.welcomeTitle')}
                </span>
              </div>
            )}
            
            {message.structured_data ? (
              <>
                {(message.presentation_type === 'structured_summary' || message.presentation_type === 'streaming_summary') ? (
                  <SummaryRenderer 
                    data={message.structured_data} 
                    isStreaming={message.presentation_type === 'streaming_summary'} 
                  />
                ) : (message.presentation_type === 'structured_results' || message.presentation_type === 'streaming_results') ? (
                  message.presentation_type === 'streaming_results' ? (
                    <StreamingResultsRenderer data={message.structured_data} />
                  ) : (
                    <StructuredResultsRenderer data={message.structured_data} />
                  )
                ) : (
                  <StructuredResultsRenderer data={message.structured_data} />
                )}
                <div className="mt-2 md:mt-3 text-xs text-gray-600">
                  {t('feedback.resultsCta.prefix')}{' '}
                  <button
                    type="button"
                    onClick={onShowFeedback}
                    className="text-emerald-600 hover:text-emerald-700 underline"
                  >
                    {t('feedback.resultsCta.linkText')}
                  </button>{' '}
                  {t('feedback.resultsCta.suffix')}
                </div>
              </>
            ) : (
              <MarkdownRenderer content={message.content} />
            )}
            
            {message.prompt && (
              <div className="mt-2 md:mt-3 p-2 md:p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="font-medium text-amber-800 text-xs md:text-sm flex items-center space-x-1">
                  <span>📝</span>
                  <span>{t('prompts.pleaseProvide')}</span>
                </div>
                <div className="text-amber-700 text-xs md:text-sm mt-1">{message.prompt}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm md:text-base">{message.content}</div>
        )}
        
        {message.toolsUsed && message.toolsUsed.length > 0 && (
          <div className="mt-1.5 md:mt-2 text-xs opacity-75">
            <div className="flex flex-wrap gap-1">
              {message.toolsUsed.map((tool: any, index: number) => (
                <span key={index} className="bg-black/10 rounded-full px-2 py-1">
                  🔧 {tool.name}
                </span>
              ))}
            </div>
          </div>
        )}
        
        <div className="text-xs opacity-60 mt-1 md:mt-2">
          {message.timestamp.toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;